import React, { useState, useEffect, useRef } from 'react';
import { MdMessage, MdSupport, MdClose, MdRefresh } from 'react-icons/md';
import { FaTicketAlt } from 'react-icons/fa';

const NotificationDropdown = ({ 
  isOpen, 
  onClose, 
  messageCount, 
  supportTicketCount, 
  onOpenChat, 
  onOpenSupport,
  onRefresh,
  loading = false
}) => {
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const totalNotifications = messageCount + supportTicketCount;

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '8px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        border: '1px solid #e0e0e0',
        minWidth: '300px',
        maxWidth: '350px',
        zIndex: 1000,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: '600',
          color: '#333'
        }}>
          Notifications
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              cursor: loading ? 'default' : 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              opacity: loading ? 0.5 : 1
            }}
            title="Refresh notifications"
          >
            <MdRefresh 
              size={18} 
              style={{ 
                color: '#666',
                animation: loading ? 'spin 1s linear infinite' : 'none'
              }} 
            />
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <MdClose size={18} style={{ color: '#666' }} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {totalNotifications === 0 ? (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
            <p style={{ margin: 0, fontSize: '14px' }}>
              No new notifications
            </p>
          </div>
        ) : (
          <div>
            {/* Messages Section */}
            {messageCount > 0 && (
              <div
                onClick={onOpenChat}
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{
                  backgroundColor: '#2196f3',
                  borderRadius: '50%',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MdMessage size={16} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '4px'
                  }}>
                    New Messages
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    You have {messageCount} unread message{messageCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {messageCount > 99 ? '99+' : messageCount}
                </div>
              </div>
            )}

            {/* Support Tickets Section */}
            {supportTicketCount > 0 && (
              <div
                onClick={onOpenSupport}
                style={{
                  padding: '16px 20px',
                  borderBottom: messageCount > 0 ? 'none' : '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{
                  backgroundColor: '#ff9800',
                  borderRadius: '50%',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaTicketAlt size={16} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '4px'
                  }}>
                    Support Tickets
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    You have {supportTicketCount} pending ticket{supportTicketCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {supportTicketCount > 99 ? '99+' : supportTicketCount}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS for spin animation */}
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NotificationDropdown;
